import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Chip, IconButton, Link, Menu, MenuItem, Typography } from '@mui/material';
import { useState } from 'react';
import TaskStatusChip from './TaskStatusChip';
import { formatDue } from './taskUi';

const SOURCE_LINK_TYPES = ['VIDEO', 'POST'];

/**
 * Title, language and status, the dates, the Source link, and the menu. pencil is how the posting
 * date changes, and only shows for someone who may change it. The date itself shows when the
 * server sends it.
 */
const TaskDrawerHeader = ({ task, current, title, onPencil, menuActions, onClose }) => {
    const [anchor, setAnchor] = useState(null);
    const closeMenu = () => setAnchor(null);
    const dates = task && !task.redacted ? task : null;
    const dueLine = dates?.dueDate && current ? `${current.displayName} due ${formatDue(dates.dueDate)}` : '';
    const hasSourceLink = dates && SOURCE_LINK_TYPES.includes(task.contentType);
    return (
        <Box sx={{ flexShrink: 0, p: 2, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Typography variant="h6" sx={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
                    {title}
                </Typography>
                {menuActions.length > 0 && (
                    <IconButton aria-label="PM actions" onClick={(e) => setAnchor(e.currentTarget)}>
                        <MoreVertIcon />
                    </IconButton>
                )}
                <IconButton aria-label="close" onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={closeMenu}>
                {menuActions.map((a) => (
                    <MenuItem
                        key={a.label}
                        onClick={() => {
                            closeMenu();
                            a.open();
                        }}
                        sx={a.danger ? { color: 'error.main' } : undefined}
                    >
                        {a.label}
                    </MenuItem>
                ))}
            </Menu>
            {task && (
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
                    <Chip size="small" label={task.languageLabel} />
                    <TaskStatusChip row={task} />
                </Box>
            )}
            {dates && (dueLine || dates.targetDate || onPencil) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75, minHeight: 28 }}>
                    <Typography variant="body2" color="text.secondary">
                        {dueLine}
                        {dueLine && (dates.targetDate || onPencil) ? ' · ' : ''}
                        {dates.targetDate ? (
                            <>
                                Posts <b>{formatDue(dates.targetDate)}</b>
                            </>
                        ) : (
                            onPencil && 'No posting date'
                        )}
                    </Typography>
                    {onPencil && (
                        <IconButton
                            size="small"
                            aria-label={dates.targetDate ? 'Change posting date' : 'Set posting date'}
                            onClick={onPencil}
                        >
                            <EditIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                    )}
                </Box>
            )}
            {hasSourceLink && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    {task.sourceUrl ? (
                        <Link href={task.sourceUrl} target="_blank" rel="noopener noreferrer">
                            Source link
                        </Link>
                    ) : (
                        'No source link'
                    )}
                </Typography>
            )}
        </Box>
    );
};

export default TaskDrawerHeader;
